import { useEffect, useState } from 'react'
import { api, ErrorDeApi } from '../servicios/api'

/**
 * Aviso de privacidad y derechos ARCO (LFPDPPP; RNF-06, RN-04 · P-102 de ADR-008/S30).
 *
 * El texto vive aquí, en la SPA; el backend solo lleva la versión vigente (debe coincidir con
 * ServicioDeAvisoDePrivacidad.VersionActual) y la constancia de quién la aceptó.
 */
const VERSION_MOSTRADA = '2026-09-05'

export function AvisoDePrivacidad() {
  const [version, setVersion] = useState<string | null>(null)
  const [aceptado, setAceptado] = useState(false)
  const [cargando, setCargando] = useState(true)
  const [aceptando, setAceptando] = useState(false)
  const [error, setError] = useState('')
  const [mensaje, setMensaje] = useState('')

  async function cargar() {
    setCargando(true)
    setError('')
    try {
      const estado = await api.avisoDePrivacidad()
      setVersion(estado.version)
      setAceptado(estado.aceptado)
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo consultar el estado de tu aceptación.')
    } finally {
      setCargando(false)
    }
  }

  useEffect(() => {
    void cargar()
  }, [])

  async function aceptar() {
    setAceptando(true)
    setError('')
    setMensaje('')
    try {
      await api.aceptarAvisoDePrivacidad()
      setAceptado(true)
      setMensaje('Aceptación registrada. Gracias.')
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo registrar tu aceptación. Intenta de nuevo.')
    } finally {
      setAceptando(false)
    }
  }

  return (
    <section aria-labelledby="titulo-aviso-privacidad">
      <header className="encabezado">
        <h1 id="titulo-aviso-privacidad">Aviso de privacidad</h1>
      </header>

      <div role="status" aria-live="polite" className="sutil">
        {cargando && 'Consultando el estado de tu aceptación…'}
        {!cargando && mensaje}
      </div>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      <article className="tarjeta" style={{ maxWidth: 760 }}>
        <p className="sutil">Versión {version ?? VERSION_MOSTRADA}</p>

        <h2>Responsable del tratamiento</h2>
        <p>
          ContaQuick es la plataforma que tu despacho contable usa para llevar tu contabilidad.
          El despacho que te da servicio es responsable del tratamiento de tus datos personales
          conforme a la Ley Federal de Protección de Datos Personales en Posesión de los
          Particulares (LFPDPPP); ContaQuick trata los datos por cuenta e instrucción del
          despacho, como encargado, únicamente para prestar el servicio.
        </p>

        <h2>Datos personales que se tratan</h2>
        <p>
          Para prestar el servicio contable se tratan datos de identificación y contacto (nombre,
          RFC, correo, teléfono), datos fiscales (comprobantes fiscales digitales por internet o
          CFDI, catálogo de cuentas, pólizas contables, declaraciones y demás información
          necesaria para el cumplimiento de obligaciones fiscales) y, cuando aplica, la e.firma
          del contribuyente, que se resguarda cifrada y solo se usa para los trámites que el
          despacho autorice.
        </p>

        <h2>Finalidades del tratamiento</h2>
        <ul>
          <li>Llevar la contabilidad electrónica del contribuyente (pólizas, catálogo de cuentas, reportes).</li>
          <li>Descargar, indexar y conservar los CFDI del contribuyente ante el SAT.</li>
          <li>Verificar el estatus fiscal (vigencia, cancelaciones) y el listado de contribuyentes del artículo 69-B.</li>
          <li>Generar los reportes y archivos que exige la contabilidad electrónica.</li>
          <li>Enviar avisos operativos (vencimientos, certificados por vencer, resultados de descargas).</li>
        </ul>

        <h2>Plazo de conservación</h2>
        <p>
          Los CFDI y demás documentación fiscal se conservan durante al menos 5 años, plazo
          mínimo exigido por la legislación fiscal mexicana; el resto de la información contable
          se conserva mientras exista una relación vigente con el despacho y, después, por el
          plazo que la ley exija.
        </p>

        <h2>Derechos ARCO y su ejercicio</h2>
        <p>
          Tienes derecho a Acceder a tus datos personales, Rectificarlos si son inexactos,
          Cancelarlos cuando consideres que no se requieren para alguna de las finalidades
          señaladas, y Oponerte al tratamiento de los mismos para fines específicos. Para
          ejercer cualquiera de estos derechos, contacta directamente a tu despacho contable —
          es quien, como responsable, puede atender y resolver tu solicitud dentro de los
          plazos que marca la ley.
        </p>

        <h2>Transferencias</h2>
        <p>
          Tus datos se transfieren al Servicio de Administración Tributaria (SAT) únicamente en
          la medida necesaria para los trámites que el despacho te presta (descarga de CFDI,
          consulta de estatus, verificación del listado 69-B), conforme lo exigen las
          disposiciones fiscales aplicables. No se transfieren a terceros con fines distintos.
        </p>

        <h2>Cambios a este aviso</h2>
        <p>
          Si el contenido de este aviso cambia de fondo, se publicará una nueva versión y se
          pedirá una nueva aceptación.
        </p>

        <div className="acciones">
          {aceptado ? (
            <p style={{ margin: 0 }}>
              <strong>Ya aceptaste esta versión del aviso de privacidad.</strong>
            </p>
          ) : (
            <button className="primario" onClick={() => void aceptar()} disabled={aceptando || cargando}>
              {aceptando ? 'Registrando…' : 'Acepto el aviso de privacidad'}
            </button>
          )}
        </div>
      </article>
    </section>
  )
}
